import { useQuery } from '@tanstack/react-query';
import type { AgentApiJobPostImportRequestsRecord } from '@/types/serializers';

type Options = {
  // true の場合、指定したステータスの間ポーリングを続ける
  pollWhileStatusIn?: AgentApiJobPostImportRequestsRecord['status'][];
  // 旧オプション: completed になるまでポーリング（互換性のため残す）
  pollUntilComplete?: boolean;
  onComplete?: () => void;
};

export default function useFetchJobPostImportRequest(
  encryptId: string | null | undefined,
  options?: Options
) {
  const token = (document.getElementsByName('csrf-token')[0] as HTMLMetaElement).content;

  return useQuery({
    queryKey: ['JobPostImportRequest', encryptId, options, token],
    queryFn: async () => {
      const response = await fetch(`/agent/api/job_post_import_requests/${encryptId}.json`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': token,
        },
      });

      if (!response.ok) {
        throw new Error('インポートリクエストの取得に失敗しました');
      }

      const data: AgentApiJobPostImportRequestsRecord = await response.json();

      // 完了時のコールバック実行
      if (options?.pollUntilComplete && data.status === 'completed' && options?.onComplete) {
        options.onComplete();
      }

      return data;
    },
    enabled: !!encryptId,
    gcTime: 0,
    staleTime: 0,
    refetchInterval: (query) => {
      const currentStatus = query.state.data?.status;

      // 特定ステータスの間だけポーリングしたい場合
      if (options?.pollWhileStatusIn && currentStatus) {
        if (options.pollWhileStatusIn.includes(currentStatus)) {
          return 2000; // 2秒ごとにポーリング
        }
        return false;
      }

      // 従来の「completed / failed になるまでポーリング」挙動
      if (
        options?.pollUntilComplete &&
        currentStatus !== 'completed' &&
        currentStatus !== 'failed'
      ) {
        return 2000; // 2秒ごとにポーリング
      }
      return false;
    },
    refetchIntervalInBackground: true,
  });
}
